'use client'

import ConfirmModal from '@/components/confirm-modal'
import PaginationControls from '@/components/pagination/PaginationControls'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import {
    createTussCode,
    deleteTussCode,
    getTussCodes,
    TussCode,
    TussFilters,
    updateTussCode,
} from '@/lib/api/tuss/tuss-api'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { MdAdd, MdDelete, MdEdit, MdHealthAndSafety, MdRefresh } from 'react-icons/md'
import { toast } from 'react-toastify'

const formatDate = (iso: string | null): string =>
    iso ? new Date(iso).toLocaleDateString('pt-BR') : '—'

interface TussForm {
    code: string
    term: string
    description: string
    validityStart: string
    validityEnd: string
    implementationEnd: string
    active: boolean
}

const EMPTY_FORM: TussForm = {
    code: '',
    term: '',
    description: '',
    validityStart: '',
    validityEnd: '',
    implementationEnd: '',
    active: true,
}

const isoToInput = (iso: string | null): string =>
    iso ? new Date(iso).toISOString().slice(0, 10) : ''

export default function TussListPage() {
    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')
    const [sourceFilter, setSourceFilter] = useState<'' | 'IMPORTED' | 'MANUAL'>('')
    const [activeFilter, setActiveFilter] = useState<'' | 'true' | 'false'>('')

    // Debounce da busca
    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim()), 300)
        return () => clearTimeout(t)
    }, [searchInput])

    const filters = useMemo<TussFilters>(
        () => ({
            search: search || undefined,
            source: sourceFilter || undefined,
            active: activeFilter || undefined,
        }),
        [search, sourceFilter, activeFilter],
    )

    const fetchFn = useCallback(
        (params: TussFilters & { page: number; limit: number }) =>
            getTussCodes(params),
        [],
    )

    const {
        items,
        loading,
        page,
        limit,
        total,
        setPage,
        reload,
    } = usePaginatedPage<TussCode, TussFilters>({
        fetchFn,
        filters,
        limit: 20,
    })

    // Modal de criar/editar
    const [modalOpen, setModalOpen] = useState(false)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [form, setForm] = useState<TussForm>(EMPTY_FORM)
    const [saving, setSaving] = useState(false)

    const openCreate = () => {
        setEditingId(null)
        setForm(EMPTY_FORM)
        setModalOpen(true)
    }

    const openEdit = (item: TussCode) => {
        setEditingId(item.id)
        setForm({
            code: item.code,
            term: item.term,
            description: item.description || '',
            validityStart: isoToInput(item.validityStart),
            validityEnd: isoToInput(item.validityEnd),
            implementationEnd: isoToInput(item.implementationEnd),
            active: item.active,
        })
        setModalOpen(true)
    }

    const handleSave = async () => {
        if (!form.code.trim()) {
            toast.warning('Informe o código TUSS')
            return
        }
        if (!form.term.trim()) {
            toast.warning('Informe o termo')
            return
        }
        setSaving(true)
        try {
            const payload = {
                code: form.code.trim(),
                term: form.term.trim(),
                description: form.description.trim() || undefined,
                validityStart: form.validityStart || undefined,
                validityEnd: form.validityEnd || undefined,
                implementationEnd: form.implementationEnd || undefined,
                active: form.active,
            }
            const res = editingId
                ? await updateTussCode(editingId, payload)
                : await createTussCode(payload)
            if (res.success) {
                toast.success(editingId ? 'Código atualizado' : 'Código adicionado')
                setModalOpen(false)
                reload()
            } else {
                toast.error(res.error || 'Erro ao salvar código TUSS')
            }
        } finally {
            setSaving(false)
        }
    }

    // Exclusão
    const [deleteTarget, setDeleteTarget] = useState<TussCode | null>(null)
    const [deleting, setDeleting] = useState(false)

    const handleDelete = async () => {
        if (!deleteTarget) return
        setDeleting(true)
        try {
            const res = await deleteTussCode(deleteTarget.id)
            if (res.success) {
                toast.success('Código removido')
                setDeleteTarget(null)
                reload()
            } else {
                toast.error(res.error || 'Erro ao remover')
            }
        } finally {
            setDeleting(false)
        }
    }

    return (
        <div className="p-4 md:p-6 max-w-7xl mx-auto">
            <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <MdHealthAndSafety /> Códigos TUSS
                    </h1>
                    <p className="text-base-content/60 text-sm mt-1">
                        Terminologia Unificada da Saúde Suplementar — Tabela 22
                        (Procedimentos e Eventos em Saúde). Catálogo importado do
                        padrão TISS/ANS, editável manualmente.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        className="btn btn-sm btn-ghost"
                        onClick={() => reload()}
                        title="Recarregar"
                    >
                        <MdRefresh className="text-base" />
                    </button>
                    <button className="btn btn-sm btn-secondary" onClick={openCreate}>
                        <MdAdd className="text-base" /> Adicionar
                    </button>
                </div>
            </div>

            {/* Filtros */}
            <div className="card bg-base-100 shadow mb-4">
                <div className="card-body p-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div className="form-control md:col-span-2">
                            <label className="label">
                                <span className="label-text">Buscar</span>
                            </label>
                            <input
                                className="input input-bordered input-sm"
                                value={searchInput}
                                onChange={e => setSearchInput(e.target.value)}
                                placeholder="Código ou termo..."
                            />
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Origem</span>
                            </label>
                            <select
                                className="select select-bordered select-sm"
                                value={sourceFilter}
                                onChange={e =>
                                    setSourceFilter(e.target.value as any)
                                }
                            >
                                <option value="">Todas</option>
                                <option value="IMPORTED">Importado</option>
                                <option value="MANUAL">Manual</option>
                            </select>
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Situação</span>
                            </label>
                            <select
                                className="select select-bordered select-sm"
                                value={activeFilter}
                                onChange={e =>
                                    setActiveFilter(e.target.value as any)
                                }
                            >
                                <option value="">Todas</option>
                                <option value="true">Ativos</option>
                                <option value="false">Inativos</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabela */}
            <div className="card bg-base-100 shadow">
                <div className="card-body p-0">
                    <div className="overflow-x-auto">
                        <table className="table table-sm">
                            <thead>
                                <tr>
                                    <th>Código</th>
                                    <th>Termo</th>
                                    <th>Vigência</th>
                                    <th>Origem</th>
                                    <th>Situação</th>
                                    <th className="text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr>
                                        <td colSpan={6} className="text-center py-8">
                                            <span className="loading loading-spinner loading-md" />
                                        </td>
                                    </tr>
                                )}
                                {!loading && items.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={6}
                                            className="text-center py-8 text-base-content/50"
                                        >
                                            Nenhum código TUSS encontrado.
                                        </td>
                                    </tr>
                                )}
                                {!loading &&
                                    items.map(item => (
                                        <tr key={item.id}>
                                            <td className="font-mono whitespace-nowrap">
                                                {item.code}
                                            </td>
                                            <td className="max-w-md">{item.term}</td>
                                            <td className="whitespace-nowrap text-xs">
                                                {formatDate(item.validityStart)} —{' '}
                                                {formatDate(item.validityEnd)}
                                            </td>
                                            <td>
                                                <span
                                                    className={`badge badge-sm ${
                                                        item.source === 'MANUAL'
                                                            ? 'badge-info'
                                                            : 'badge-ghost'
                                                    }`}
                                                >
                                                    {item.source === 'MANUAL'
                                                        ? 'Manual'
                                                        : 'Importado'}
                                                </span>
                                            </td>
                                            <td>
                                                <span
                                                    className={`badge badge-sm ${
                                                        item.active
                                                            ? 'badge-success'
                                                            : 'badge-error'
                                                    }`}
                                                >
                                                    {item.active
                                                        ? 'Ativo'
                                                        : 'Inativo'}
                                                </span>
                                            </td>
                                            <td className="text-right whitespace-nowrap">
                                                <button
                                                    className="btn btn-xs btn-ghost"
                                                    onClick={() => openEdit(item)}
                                                    title="Editar"
                                                >
                                                    <MdEdit className="text-base" />
                                                </button>
                                                <button
                                                    className="btn btn-xs btn-ghost text-error"
                                                    onClick={() =>
                                                        setDeleteTarget(item)
                                                    }
                                                    title="Remover"
                                                >
                                                    <MdDelete className="text-base" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                    {total > 0 && (
                        <div className="px-4">
                            <PaginationControls
                                page={page}
                                limit={limit}
                                total={total}
                                loading={loading}
                                onPageChange={setPage}
                                label="código"
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Modal criar/editar */}
            {modalOpen && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-2xl">
                        <h3 className="font-bold text-lg mb-4">
                            {editingId ? 'Editar código TUSS' : 'Adicionar código TUSS'}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Código *</span>
                                </label>
                                <input
                                    className="input input-bordered input-sm font-mono"
                                    value={form.code}
                                    onChange={e =>
                                        setForm({ ...form, code: e.target.value })
                                    }
                                    placeholder="Ex: 10101012"
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Situação</span>
                                </label>
                                <select
                                    className="select select-bordered select-sm"
                                    value={form.active ? 'true' : 'false'}
                                    onChange={e =>
                                        setForm({
                                            ...form,
                                            active: e.target.value === 'true',
                                        })
                                    }
                                >
                                    <option value="true">Ativo</option>
                                    <option value="false">Inativo</option>
                                </select>
                            </div>
                            <div className="form-control md:col-span-2">
                                <label className="label">
                                    <span className="label-text">Termo *</span>
                                </label>
                                <input
                                    className="input input-bordered input-sm"
                                    value={form.term}
                                    onChange={e =>
                                        setForm({ ...form, term: e.target.value })
                                    }
                                    placeholder="Nome do procedimento/evento"
                                />
                            </div>
                            <div className="form-control md:col-span-2">
                                <label className="label">
                                    <span className="label-text">
                                        Descrição detalhada
                                    </span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered textarea-sm"
                                    rows={3}
                                    value={form.description}
                                    onChange={e =>
                                        setForm({
                                            ...form,
                                            description: e.target.value,
                                        })
                                    }
                                    placeholder="Opcional"
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">
                                        Início de vigência
                                    </span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered input-sm"
                                    value={form.validityStart}
                                    onChange={e =>
                                        setForm({
                                            ...form,
                                            validityStart: e.target.value,
                                        })
                                    }
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">
                                        Fim de vigência
                                    </span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered input-sm"
                                    value={form.validityEnd}
                                    onChange={e =>
                                        setForm({
                                            ...form,
                                            validityEnd: e.target.value,
                                        })
                                    }
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">
                                        Fim de implantação
                                    </span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered input-sm"
                                    value={form.implementationEnd}
                                    onChange={e =>
                                        setForm({
                                            ...form,
                                            implementationEnd: e.target.value,
                                        })
                                    }
                                />
                            </div>
                        </div>
                        <div className="modal-action">
                            <button
                                className="btn btn-sm btn-ghost"
                                onClick={() => setModalOpen(false)}
                                disabled={saving}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-sm btn-secondary"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving && (
                                    <span className="loading loading-spinner loading-xs" />
                                )}
                                Salvar
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => !saving && setModalOpen(false)}
                    />
                </div>
            )}

            <ConfirmModal
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                loading={deleting}
                title="Remover código TUSS"
                message={`Deseja remover o código ${deleteTarget?.code} - ${deleteTarget?.term}?`}
                confirmButtonText="Remover"
            />
        </div>
    )
}
